import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useId, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND, NAV } from "../data/content";
import { IDS } from "../lib/testIds";
import BrandLogo from "./BrandLogo";

const FOCUS_RING =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-navy";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const location = useLocation();
  const panelId = useId();
  const toggleRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);

    const firstLink = panelRef.current?.querySelector("a");
    firstLink?.focus();

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <header
      data-testid={IDS.nav.root}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,box-shadow,border-color] duration-500 ${
        scrolled
          ? "bg-cream/90 backdrop-blur-md border-b border-warmborder"
          : "bg-cream border-b border-transparent"
      }`}
    >
      <div className="page-container h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid={IDS.nav.logo}
          className={`group inline-flex items-center rounded-sm ${FOCUS_RING}`}
          aria-label={`${BRAND.name} — Home`}
        >
          <BrandLogo />
        </Link>

        <nav className="hidden md:flex items-center gap-9" aria-label="Principale">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={IDS.nav.link(n.to.replace("/", ""))}
              className={({ isActive }) =>
                `text-[13px] tracking-overline uppercase font-medium link-underline rounded-sm ${FOCUS_RING} ${
                  isActive ? "text-navy" : "text-ink/80 hover:text-navy"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block">
          <Link
            to="/gadget"
            data-testid={IDS.nav.cta}
            className={`group inline-flex items-center gap-2 bg-navy text-cream px-5 py-2.5 text-[12px] tracking-overline uppercase font-medium hover:bg-ink transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-lemon`}
          >
            Vedi gadget
            <span aria-hidden className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
          </Link>
        </div>

        <button
          ref={toggleRef}
          type="button"
          className={`md:hidden inline-flex items-center justify-center min-h-11 min-w-11 -mr-2 text-navy rounded-sm ${FOCUS_RING}`}
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Chiudi menu" : "Apri menu"}
          aria-expanded={open}
          aria-controls={panelId}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} aria-hidden /> : <Menu size={22} aria-hidden />}
        </button>
      </div>

      <AnimatePresence initial={false}>
        {open && (() => {
          const Panel = reduceMotion ? "div" : motion.div;
          const motionProps = reduceMotion
            ? {}
            : {
                initial: { height: 0, opacity: 0 },
                animate: { height: "auto", opacity: 1 },
                exit: { height: 0, opacity: 0 },
                transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
              };
          return (
            <Panel
              id={panelId}
              ref={panelRef}
              role="navigation"
              aria-label="Menu mobile"
              className="md:hidden overflow-hidden bg-cream border-t border-warmborder"
              {...motionProps}
            >
              <div className="px-4 sm:px-6 py-6 flex flex-col gap-5">
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    className={`font-display text-3xl text-navy rounded-sm ${FOCUS_RING}`}
                    data-testid={`nav-mobile-${n.to.replace("/", "")}`}
                  >
                    {n.label}
                  </NavLink>
                ))}
                <Link
                  to="/gadget"
                  className="mt-2 inline-flex items-center gap-2 bg-navy text-cream px-5 py-3 w-fit text-[12px] tracking-overline uppercase font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-lemon"
                >
                  Vedi gadget →
                </Link>
              </div>
            </Panel>
          );
        })()}
      </AnimatePresence>
    </header>
  );
}
